import type {
  AgentAnalyticsResponse,
  Metric,
} from '../../../../../services/conversations-billing/models/AgentAnalyticsResponse';
import type { MetricCardData } from '../components/AnalyticsCard';
import type { PieChartData } from '../components/PieChartCard';
import type { BarChartData } from '../components/BarChartCard';
import {
  metricCardTemplates,
  pieChartTemplates,
  chartTemplates,
} from '../config/cardTemplates';
import { formatDateByScale } from './dateFormatting';
import { formatDurationValue, convertValueByType } from './valueFormatting';
import { MetricCardType, AnalyticsMetric } from '../../../const';
import { MetricsWithTags } from '../../../../conversations/const';

export const processAnalyticsData = (
  analyticsData: AgentAnalyticsResponse | null
) => {
  if (!analyticsData || !analyticsData.metrics) {
    return {
      metrics: metricCardTemplates.map(template => ({
        ...template,
        value: '0',
        unit: template.unit || '',
      })),
      pieCharts: pieChartTemplates.map(template => {
        if (template.type === AnalyticsMetric.SYSTEM_AVAILABILITY) {
          return {
            ...template,
            data: template.data.map((item: any) => ({ ...item })),
          };
        }
        if (MetricsWithTags.includes(template.type)) {
          return {
            ...template,
            data: [],
          };
        }
        return {
          ...template,
          data: [],
        };
      }),
      charts: chartTemplates.map(template => ({
        ...template,
        data: [],
      })),
    };
  }

  const metricsMap = new Map<string, Metric>();
  analyticsData.metrics.forEach(metric => {
    metricsMap.set(metric.name, metric);
  });

  const processedMetrics: MetricCardData[] = metricCardTemplates.map(
    template => {
      const metric = metricsMap.get(template.type);
      if (!metric || metric.series.length === 0) {
        return {
          ...template,
          value: '0',
          unit: template.unit || '',
        };
      }

      let value: string | number | { minutes: number; seconds: number } = '0';
      let unit: string | { minute: string; seconds: string } =
        template.unit || '';

      const mainValue = parseFloat(metric.value || '0');

      switch (template.type) {
        case MetricCardType.DURATION_SECONDS:
          value = formatDurationValue(mainValue);
          unit = { minute: 'min', seconds: 'sec' };
          break;
        case MetricCardType.AVERAGE_RESPONSE_TIME_SECONDS:
          const convertedValue = convertValueByType(mainValue, template.type);
          value = (Math.floor(convertedValue * 10) / 10).toFixed(1);
          unit = 'sec';
          break;
        case MetricCardType.CSAT_STARS:
          value = mainValue.toFixed(1);
          unit = '/5';
          break;
        default:
          value = mainValue.toString();
      }

      const processedSeriesData = metric.series.map(seriesItem => {
        const rawValue = parseFloat(seriesItem.value) || 0;
        const processedValue = convertValueByType(rawValue, template.type);

        return {
          ...seriesItem,
          value: processedValue.toString(),
          metricType: template.type,
        };
      });

      return {
        ...template,
        value,
        unit,
        seriesData: processedSeriesData,
        scale: metric.scale,
      };
    }
  );

  const processedPieCharts: PieChartData[] = pieChartTemplates.map(template => {
    const metric = metricsMap.get(template.type);
    if (!metric || metric.series.length === 0) {
      if (template.type === AnalyticsMetric.SYSTEM_AVAILABILITY) {
        return {
          ...template,
          data: template.data.map(item => ({ ...item })),
        };
      }
      return {
        ...template,
        data: [],
      };
    }

    const mainValue = parseFloat(metric.value || '0');

    const seriesSum = !MetricsWithTags.includes(template.type)
      ? metric.numberOfConversations
      : undefined;

    if (MetricsWithTags.includes(template.type)) {
      const topicTagsData: Array<{
        name: string;
        value: number;
        originalValue: number;
        percentage: number;
        color: string;
      }> = [];
      const colors = [
        '#8B5CF6',
        '#06E17E',
        '#F14E42',
        '#0E88E9',
        '#10b981',
        '#F59E0B',
        '#EF4444',
        '#3B82F6',
        '#EC4899',
        '#14B8A6',
        '#F97316',
        '#84CC16',
        '#A855F7',
        '#06B6D4',
      ];

      const tagCounts: { [key: string]: number } = {};
      const tagDisplayNames: { [key: string]: string } = {};

      metric.series.forEach(seriesItem => {
        if (typeof seriesItem.value === 'object' && seriesItem.value !== null) {
          Object.entries(seriesItem.value).forEach(([labelName, count]) => {
            const countNum = parseFloat(count as string) || 0;
            const normalizedLabelName = labelName.toLowerCase();
            tagCounts[normalizedLabelName] =
              (tagCounts[normalizedLabelName] || 0) + countNum;

            if (!tagDisplayNames[normalizedLabelName]) {
              tagDisplayNames[normalizedLabelName] = labelName;
            }
          });
        }
      });

      const totalSum = Object.values(tagCounts).reduce(
        (sum, count) => sum + count,
        0
      );

      Object.entries(tagCounts).forEach(
        ([normalizedLabelName, count], index) => {
          const percentage = totalSum > 0 ? (count / totalSum) * 100 : 0;
          const displayName =
            tagDisplayNames[normalizedLabelName] || normalizedLabelName;
          topicTagsData.push({
            name: displayName
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase()),
            value: Math.round(percentage * 10) / 10,
            originalValue: Math.round(count * 10) / 10,
            percentage: Math.round(percentage * 10) / 10,
            color: colors[index % colors.length],
          });
        }
      );

      topicTagsData.sort((a, b) => b.originalValue - a.originalValue);

      const totalPercentage = topicTagsData.reduce(
        (sum, item) => sum + item.value,
        0
      );

      if (totalPercentage < 100) {
        const remainingPercentage = 100 - totalPercentage;
        topicTagsData.push({
          name: 'Empty',
          value: Math.round(remainingPercentage * 10) / 10,
          originalValue: 0,
          percentage: Math.round(remainingPercentage * 10) / 10,
          color: '#DDE5E8',
        });
      }

      return {
        ...template,
        data: topicTagsData,
        totalSum: totalSum,
        numberOfConversations: seriesSum,
      };
    }

    const processedValue = Math.round(mainValue * 10) / 10;
    const remainingValue = Math.round((100 - processedValue) * 10) / 10;

    const totalConversations = seriesSum || 0;
    const selectedConversations = Math.round(
      (processedValue / 100) * totalConversations
    );
    const remainingConversations = totalConversations - selectedConversations;

    const processedSeriesData = metric.series.map(seriesItem => ({
      ...seriesItem,
      value: seriesItem.value,
    }));

    return {
      ...template,
      data: [
        {
          ...template.data[0],
          value: processedValue,
          percentage: processedValue,
          originalValue: mainValue,
          selectedConversations: selectedConversations,
        },
        {
          ...template.data[1],
          value: remainingValue,
          percentage: remainingValue,
          originalValue: 100 - mainValue,
          selectedConversations: remainingConversations,
        },
      ],
      seriesData: processedSeriesData,
      scale: metric.scale,
      numberOfConversations: seriesSum,
    };
  });

  const processedCharts: BarChartData[] = chartTemplates.map(template => {
    const metricNameMap: { [key: string]: string } = {
      [AnalyticsMetric.MAX_CONCURRENCY]: 'concurrent_conversation_count',
      [AnalyticsMetric.HUMAN_ESCALATION]: 'human_escalation',
    };

    const actualMetricName = metricNameMap[template.type] || template.type;
    const metric = metricsMap.get(actualMetricName);
    if (!metric || metric.series.length === 0) {
      return {
        ...template,
        data: [],
      };
    }

    const processedData = metric.series.map(item => {
      const date = new Date(item.bucket);
      const displayName = formatDateByScale(date, metric.scale);

      return {
        name: displayName,
        value: parseFloat(item.value) || 0,
        timestamp: item.bucket,
        metricType: template.type,
      };
    });

    return {
      ...template,
      data: processedData,
      scale: metric.scale || undefined,
    };
  });

  const result = {
    metrics: processedMetrics,
    pieCharts: processedPieCharts,
    charts: processedCharts,
  };

  return result;
};
